import React, { useState, useEffect } from "react";
import axios from "axios";
import {
    Table,
    Button,
    Card,
    Row,
    Col,
    Container,
    Spinner,
} from "react-bootstrap";
import { FaEllipsisV } from "react-icons/fa";
import Breadcrumbs from "../../Helper/Breadcrumbs";
import { useHistory } from "react-router-dom";
import ConfirmationModel from "../../../shared/ConfirmationModel";
import config from "../../../config";
import { Menu, MenuItem } from "@mui/material";
import PaginationComponent from "./PaginationComponent";
import { toast, ToastContainer } from "react-toastify";
import { toastAttr } from "./utils";

const UnlistedLeadsList = () => {
    const history = useHistory();
    const [leads, setLeads] = useState([]);
    const [currentLead, setCurrentLead] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [openConfirmation, setOpenConfirmation] = useState(false);
    const [selectedLeadId, setSelectedLeadId] = useState(null);

    const [pagination, setPagination] = useState({
        page: 1,
        pageSize: 10,
        totalPages: 1,
    });

    const tokenConfig = {
        headers: { Authorization: `Token ${sessionStorage.getItem("tokenKey")}` },
    };

    const breadcrumbData = [
        { title: "Offline Leads", href: "/offlineleads" },
        { title: "Unlisted Venue Leads", href: "#" },
    ];

    const fetchLeads = () => {
        setLoading(true);
        setError(null);
        const permissionRaw = sessionStorage.getItem('permission');
        let userData = null;
        try {
            const parsed = permissionRaw ? JSON.parse(permissionRaw) : null;
            userData = Array.isArray(parsed) ? parsed[0] : parsed;
        } catch (e) {
            console.error("Error parsing permission from session storage", e);
        }

        const userId = userData?.id;
        const sessionUserId = userData?.user_id;

        const params = {
            page: pagination.page,
            page_size: pagination.pageSize,
        };

        axios
            .get(`${config.service}/leads_v2/leads/`, {
                params: params,
                ...tokenConfig,
            })
            .then(({ data }) => {
                let fetchedLeads = data.results || (Array.isArray(data) ? data : []);

                // Frontend fallback: Only show leads for the current user if the backend didn't filter correctly
                if (userId || sessionUserId) {
                    const originalCount = fetchedLeads.length;
                    fetchedLeads = fetchedLeads.filter(lead => {
                        const leadKaeId = lead.kae?.id || lead.kae;
                        const leadUserId = lead.kae?.user_id || lead.kae?.user?.id;
                        const leadCreatedBy = lead.created_by;

                        const matchId = userId && (String(leadKaeId) === String(userId) || String(leadCreatedBy) === String(userId));
                        const matchUserId = sessionUserId && (String(leadUserId) === String(sessionUserId) || String(leadCreatedBy) === String(sessionUserId));

                        return matchId || matchUserId;
                    });
                    console.log(`Cross-check: Found ${fetchedLeads.length} matches out of ${originalCount} leads for Session User (id: ${userId}, user_id: ${sessionUserId})`);
                }

                setLeads(fetchedLeads);
                setPagination((prev) => ({
                    ...prev,
                    totalPages: data.count ? Math.ceil(data.count / prev.pageSize) : 1,
                }));
            })
            .catch((err) => {
                console.error(err);
                const errorMsg = err.response?.data?.details || err.message || "An unexpected error occurred.";
                setError(errorMsg);
            })
            .finally(() => setLoading(false));
    };

    useEffect(fetchLeads, [pagination.page]);

    const handleMenuOpen = (event, lead) => {
        setAnchorEl(event.currentTarget);
        setCurrentLead(lead);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleEdit = () => {
        handleMenuClose();
        history.push({
            pathname: "/offlineleadsform",
            state: { id: currentLead.id, editMode: true, isUnlisted: true },
        });
    };

    const handleDeleteConfirmation = (leadId) => {
        handleMenuClose();
        setSelectedLeadId(leadId);
        setOpenConfirmation(true);
    };

    const handleDelete = async () => {
        try {
            await axios.delete(
                `${config.service}/leads_v2/leads/${selectedLeadId}/`,
                tokenConfig
            );

            setLeads((prevLeads) =>
                prevLeads.filter((lead) => lead.id !== selectedLeadId)
            );

            toast.success("Unlisted lead deleted successfully!", toastAttr);
            setOpenConfirmation(false);
            fetchLeads();
        } catch (error) {
            console.error("Error during unlisted lead deletion:", error);
            toast.error("Failed to delete the lead.", toastAttr);
        } finally {
            setOpenConfirmation(false);
        }
    };

    return (
        <div>
            <Breadcrumbs data={breadcrumbData} />
            <Container>
                <Card>
                    <Card.Body className="theme-light">
                        <Row className="formBtnsDiv mb-3">
                            <Col md={6}>
                                <Button
                                    variant="secondary"
                                    onClick={() => history.push("/offlineleads")}
                                >
                                    Back to Offline Leads
                                </Button>
                                <Button
                                    variant="primary"
                                    className="ms-2"
                                    onClick={() => history.push({ pathname: "/offlineleadsform", state: { isUnlisted: true } })}
                                >
                                    Create Unlisted Lead
                                </Button>
                            </Col>
                        </Row>

                        {loading ? (
                            <div className="text-center p-5">
                                <Spinner animation="border" variant="primary" />
                                <p className="mt-2">Loading unlisted leads...</p>
                            </div>
                        ) : error ? (
                            <div className="text-center p-5 text-danger border rounded bg-light">
                                <h5 className="mb-3">Failed to load leads from server</h5>
                                <div className="p-3 mb-3 bg-white border rounded text-start" style={{ maxHeight: '200px', overflowY: 'auto', fontSize: '14px', fontFamily: 'monospace' }}>
                                    {error}
                                </div>
                                <p className="mb-3 text-muted">This error usually requires a backend fix (see implementation plan).</p>
                                <Button variant="outline-primary" onClick={fetchLeads}>Retry Fetch</Button>
                            </div>
                        ) : (
                            <>
                                <Table striped bordered hover responsive>
                                    <thead>
                                        <tr>
                                            <th>Agent Name</th>
                                            <th>Venue Name</th>
                                            <th>Location</th>
                                            <th>Customer Name</th>
                                            <th>Event Type</th>
                                            <th>Event Date</th>
                                            <th>Remarks</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(!Array.isArray(leads) || leads.length === 0) ? (
                                            <tr>
                                                <td colSpan="8" className="text-center">
                                                    No unlisted leads found.
                                                </td>
                                            </tr>
                                        ) : (
                                            leads.map((lead) => (
                                                <tr key={lead.id}>
                                                    <td>{lead.kae?.name || lead.kae?.username || "N/A"}</td>
                                                    <td>{lead.unlisted_venue_name || "N/A"}</td>
                                                    <td>{lead.unlisted_venue_location || "N/A"}</td>
                                                    <td>{lead.customer_name || "N/A"}</td>
                                                    <td>{lead.primary_event_type || "N/A"}</td>
                                                    <td>{lead.primary_event_date || "N/A"}</td>
                                                    <td>{lead.remarks || "N/A"}</td>
                                                    <td>
                                                        <Button
                                                            variant="link"
                                                            onClick={(e) => handleMenuOpen(e, lead)}
                                                        >
                                                            <FaEllipsisV />
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </Table>
                                <PaginationComponent
                                    currentPage={pagination.page}
                                    totalPages={pagination.totalPages}
                                    onPageChange={(page) =>
                                        setPagination((prev) => ({ ...prev, page }))
                                    }
                                />
                            </>
                        )}

                        <Menu
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl)}
                            onClose={handleMenuClose}
                        >
                            <MenuItem onClick={() => currentLead && handleEdit()}>Edit</MenuItem>
                            <MenuItem
                                onClick={() =>
                                    currentLead && handleDeleteConfirmation(currentLead.id)
                                }
                            >
                                Delete
                            </MenuItem>
                        </Menu>

                        <ConfirmationModel
                            operation="delete"
                            handleConfirmation={handleDelete}
                            val={openConfirmation}
                            modelName="Unlisted Lead"
                            onclose={() => setOpenConfirmation(false)}
                        />
                    </Card.Body>
                </Card>
            </Container>
            <ToastContainer {...toastAttr} />
        </div>
    );
};

export default UnlistedLeadsList;
